import DownloadCSVMenuButton, {
    downloadTypes,
} from 'components/Button/DownloadCSVMenuButton';
import { MdOutlineShowChart, MdOutlineStackedLineChart } from 'react-icons/md';
import { Message, isDataSupported } from '../utils';
import { TIKTOK_METADATA_BY_KEY, TIKTOK_SORT_OPTIONS } from 'utils/metrics';
import {
    TikTokDrawerContent,
    TikTokDrawerContentIndividualHashtag,
} from 'views/TikTokTrends/TikTokDrawerContent';
import {
    buildExportFilename,
    downloadItemsAsCSV,
    downloadTimeSeriesAsCSV,
} from 'utils/formatCSV';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCurrentCountry, useEndDate, useTikTokWeeks } from 'utils/hooks';
import AddBySearch from 'components/Navigation/Search/AddBySearch';
import { BannerRow as BannerRowContent } from './BannerRow';
import { DATA_SOURCES } from '@/config/constants';
import DataTable from 'components/DataVis/DataTable/DataTable';
import DimensionIcon from 'components/DimensionIcon';
import Drawer from 'components/Drawer';
import ErrorBoundary from 'containers/ErrorBoundary';
import FilterSelect from 'components/Input/FilterSelect';
import IconButton from 'components/Button/IconButton';
import NameCellWithTooltip from '../NameCellWithTooltip';
import Stack from 'components/Stack';
import RelatedAggregateDrawerContent from 'components/DataVis/RelatedCategoriesTable/RelatedAggregateDrawerContent';
import WatchlistTikTokTimeSeries from 'components/DataVis/WatchlistTikTokTimeSeries';
import { calculateTikTokData } from 'utils/helpers';
import { createDefaultColumns } from 'components/DataVis/DataTable/utils';
import downloadImage from 'utils/downloadImage';
import { getOnChange } from 'components/SectionTableFilterChips';
import { getSeriesColors } from 'components/DataVis/Charts/MultiPredictionTimeSeriesChart';
import { getWatchlistRenderHits } from '../Buttons';
import { healthStatFilterOptions } from 'utils/filters';
import isEqual from 'react-fast-compare';
import styled from 'styled-components';
import { useCardsFilterSort } from 'components/CardsWrapper';
import useTableColumns from 'utils/hooks/useTableColumns';
import { useTikTokWatchlistMetricsData } from './useTikTokWatchlistMetricsData';
import { useTikTokWatchlistTimeSeriesData } from './useTikTokWatchlistTimeSeriesData';

const INITIAL_NUM_LINES = 12;

const tiktokMetrics = [
    [TIKTOK_METADATA_BY_KEY.non_cum_avg_weekly_views, true],
    [TIKTOK_METADATA_BY_KEY.views, true],
    [TIKTOK_METADATA_BY_KEY.non_cum_increase_WoW, false],
    [TIKTOK_METADATA_BY_KEY.non_cum_WoW, true],
    [TIKTOK_METADATA_BY_KEY.non_cum_increase_MoM_rolling, false],
    [TIKTOK_METADATA_BY_KEY.non_cum_MoM_rolling, false],
    [TIKTOK_METADATA_BY_KEY.non_cum_increase_QoQ_rolling, false],
    [TIKTOK_METADATA_BY_KEY.non_cum_QoQ_rolling, true],
    [TIKTOK_METADATA_BY_KEY.non_cum_increase_YoY_rolling, false],
    [TIKTOK_METADATA_BY_KEY.non_cum_YoY_rolling, false],
    [TIKTOK_METADATA_BY_KEY.total_posts_cnt, true],
    [TIKTOK_METADATA_BY_KEY.partner_paid_partnership_posts_views_prc, true],
    [TIKTOK_METADATA_BY_KEY.eligible_for_commission_posts_views_prc, false],
    [TIKTOK_METADATA_BY_KEY.engagement_prc, false],
    [TIKTOK_METADATA_BY_KEY.sentiment_score, false],
];
const tiktokSortOptions = Object.values(TIKTOK_SORT_OPTIONS).filter(
    ({ sortBy }) => tiktokMetrics.some(([{ key }]) => key === sortBy),
);

const topicHealthStatKeys = [
    'average weekly views',
    'paid views',
    'sentiment score',
    'posts',
    'engagement',
    'TikTok shop',
];
const tikTokHealthStatFilterOptions = healthStatFilterOptions.filter(
    ({ label }) => topicHealthStatKeys.includes(label),
);

const TableTools = styled(Stack).attrs(
    ({ $direction = 'row', $space = '.5em' }) => ({
        $direction,
        $space,
    }),
)`
    display: flex;
    justify-content: flex-end;
`;

const TiktokWatchlist = memo(
    ({
        brandIds,
        categoryIds,
        hashtagCategoryIds,
        hashtagBrandIds,
        relatedCategoryParentCategoryItems = [],
        relatedCategoryParentBrandItems = [],
        categoryPairs = [],
        brandPairs = [],
        countryTextId,
        watchlistId,
        watchlistItems,
        onRemoveItem,
        onAddItem,
        watchlistName,
    }) => {
        const country = useCurrentCountry();
        const { week, year } = useTikTokWeeks();
        const date = useEndDate('tiktok');
        const defaultInitialSortOption = tiktokSortOptions.find(
            ({ sortBy }) => sortBy === tiktokMetrics[0][0].key,
        );
        const timeSeriesRef = useRef();
        const [placeholderTsDataByType, setPlaceholderTsDataByType] = useState({
            category: [],
            brand: [],
            hashtagBrand: [],
            hashtagCategory: [],
        });
        const [placeholderMetricsDataByType, setPlaceholderMetricsDataByType] =
            useState({
                category: [],
                brand: [],
                hashtagBrand: [],
                hashtagCategory: [],
                relatedCategory: [],
            });
        const {
            metricsData,
            brandsDataQuery,
            categoriesDataQuery,
            hashtagBrandsDataQuery,
            hashtagCategoriesDataQuery,
            relatedCategoryPairsQuery,
            allRelatedCategories,
        } = useTikTokWatchlistMetricsData({
            country,
            categoryIds,
            brandIds,
            hashtagBrandIds,
            hashtagCategoryIds,
            relatedCategoryParentCategoryItems,
            relatedCategoryParentBrandItems,
            categoryPairs,
            brandPairs,
            placeholderMetricsDataByType,
            defaultInitialSortOption,
            week,
            year,
        });
        const {
            parseAllTikTokData,
            tsBrandsQuery,
            tsCategoriesQuery,
            tsHashtagBrandsQuery,
            tsHashtagCategoriesQuery,
        } = useTikTokWatchlistTimeSeriesData({
            country,
            categoryIds,
            brandIds,
            hashtagBrandIds,
            hashtagCategoryIds,
            placeholderTsDataByType,
        });
        const isPlaceholderDataLoaded =
            (categoryIds && tsCategoriesQuery.isPlaceholderData) ||
            (brandIds && tsBrandsQuery.isPlaceholderData) ||
            (categoryIds && categoriesDataQuery.isPlaceholderData) ||
            (brandIds && brandsDataQuery.isPlaceholderData) ||
            (hashtagCategoryIds &&
                hashtagCategoriesDataQuery.isPlaceholderData) ||
            (hashtagBrandIds && hashtagBrandsDataQuery.isPlaceholderData);
        let handleQueryCacheOnRemoveItem = onRemoveItem;
        const isDataLoaded =
            !(
                tsBrandsQuery.isLoading ||
                tsCategoriesQuery.isLoading ||
                brandsDataQuery.isLoading ||
                categoriesDataQuery.isLoading ||
                hashtagBrandsDataQuery.isLoading ||
                hashtagCategoriesDataQuery.isLoading ||
                tsHashtagCategoriesQuery.isLoading ||
                tsHashtagBrandsQuery.isLoading ||
                relatedCategoryPairsQuery.isLoading ||
                !country
            ) || isPlaceholderDataLoaded;
        if (onRemoveItem && (isDataLoaded || isPlaceholderDataLoaded)) {
            handleQueryCacheOnRemoveItem = (node) => {
                setPlaceholderMetricsDataByType({
                    brand: (brandsDataQuery?.data || []).filter(
                        (d) => d.brand !== node.id,
                    ),
                    category: (categoriesDataQuery?.data || []).filter(
                        (d) => d.category !== node.id,
                    ),
                    hashtagBrand: (hashtagBrandsDataQuery?.data || []).filter(
                        (d) => d.hashtag !== node.hashtag,
                    ),
                    hashtagCategory: (
                        hashtagCategoriesDataQuery?.data || []
                    ).filter((d) => d.hashtag !== node.hashtag),
                    relatedCategory: (allRelatedCategories || []).filter(
                        (d) => d.id !== node.id,
                    ),
                });
                setPlaceholderTsDataByType({
                    brand: (tsBrandsQuery?.data?.data || []).filter(
                        (d) => d.brand !== node.id,
                    ),
                    category: (tsCategoriesQuery?.data?.data || []).filter(
                        (d) => d.category !== node.id,
                    ),
                    hashtagBrand: (
                        tsHashtagBrandsQuery?.data?.data || []
                    ).filter((d) => d.hashtag !== node.hashtag),
                    hashtagCategory: (
                        tsHashtagCategoriesQuery?.data?.data || []
                    ).filter((d) => d.hashtag !== node.hashtag),
                });
                onRemoveItem(node);
            };
        } else if (onRemoveItem) {
            handleQueryCacheOnRemoveItem = () => {};
        }
        const handleQueryCacheOnAddItem = (node) => {
            const skeletonNode = {
                id: node.id || node.name,
                fetching: true,
                fetchingData: true,
                [node.type]: node.id || node.name,
                type: node.type,
                ...(node.type === 'hashtag' && node.brandId
                    ? { brand: node.brandId }
                    : {}),
            };
            setPlaceholderTsDataByType({
                brand: tsBrandsQuery?.data?.data || [],
                category: tsCategoriesQuery?.data?.data || [],
                hashtagBrand: tsHashtagBrandsQuery?.data?.data || [],
                hashtagCategory: tsHashtagCategoriesQuery?.data?.data || [],
            });
            const brand = JSON.parse(
                JSON.stringify(brandsDataQuery?.data || []),
            );
            const category = JSON.parse(
                JSON.stringify(categoriesDataQuery?.data || []),
            );
            const hashtagBrand = JSON.parse(
                JSON.stringify(hashtagBrandsDataQuery?.data || []),
            );
            const hashtagCategory = JSON.parse(
                JSON.stringify(hashtagCategoriesDataQuery?.data || []),
            );
            if (node.type === 'category') {
                category.push(skeletonNode);
            } else if (node.type === 'brand') {
                brand.push(skeletonNode);
            } else if (node.type === 'hashtag' && node.brandId) {
                hashtagBrand.push(skeletonNode);
            } else if (node.type === 'hashtag' && node.categoryId) {
                hashtagCategory.push(skeletonNode);
            }
            setPlaceholderMetricsDataByType({
                brand,
                category,
                hashtagBrand,
                hashtagCategory,
            });
            onAddItem(node);
        };
        const renderHits = useMemo(
            () =>
                getWatchlistRenderHits({
                    watchlistItems,
                    onAddItem: onAddItem ? handleQueryCacheOnAddItem : null,
                    onRemoveItem: onRemoveItem
                        ? (item) => {
                              // Recover the hashtag's UUID for the optimistic
                              // cache filter. The item can be in the watchlist
                              // (matched by name) but absent from the fetched
                              // data, so guard the lookup and keep the existing
                              // id when it misses — removal still works by name.
                              if (item.type === 'hashtag' && item.categoryId) {
                                  item.id =
                                      hashtagCategoriesDataQuery?.data?.find(
                                          ({ name }) => item.name === name,
                                      )?.hashtag ?? item.id;
                              } else if (
                                  item.type === 'hashtag' &&
                                  item.brandId
                              ) {
                                  item.id =
                                      hashtagBrandsDataQuery?.data?.find(
                                          ({ name }) => item.name === name,
                                      )?.hashtag ?? item.id;
                              }
                              const node = {
                                  [item.type]: item.id,
                                  id: item.id,
                                  type: item.type,
                                  name: item.name,
                                  [item.brandId ? 'brand' : 'category']:
                                      item.brandId || item.categoryId,
                              };
                              handleQueryCacheOnRemoveItem(node);
                          }
                        : null,
                    dataSource: 'tiktok',
                }),
            [
                watchlistItems,
                onAddItem,
                onRemoveItem,
                parseAllTikTokData,
                metricsData,
            ],
        );
        const tikTokDataMap =
            parseAllTikTokData.length &&
            parseAllTikTokData.reduce((acc, entry) => {
                acc[entry.hashtag || entry.category || entry.brand] = entry;
                return acc;
            }, {});
        const unifiedTimeSeriesAndMetricsData = useMemo(() => {
            if (!parseAllTikTokData.length && !metricsData) return [];
            return (metricsData || [])
                .map((metric) => {
                    // id is used to match node deleted from table
                    let id = null;
                    let name = null;
                    let matchingEntry = null;

                    switch (metric.type) {
                        case 'hashtag':
                            id = metric.hashtag;
                            name = metric.name;
                            matchingEntry = tikTokDataMap[metric.hashtag];
                            break;
                        case 'related-category':
                            id = metric.id;
                            name = metric.name;
                            break;
                        case 'category':
                            id = metric.category;
                            name = metric.entity;
                            matchingEntry = tikTokDataMap[metric.category];
                            break;
                        case 'brand':
                            id = metric.brand;
                            name = metric.entity;
                            matchingEntry = tikTokDataMap[metric.brand];
                            break;
                        default:
                            console.warn(`Unknown metric type: ${metric.type}`);
                            break;
                    }

                    return {
                        ...metric,
                        id,
                        name,
                        time_series: matchingEntry
                            ? matchingEntry.time_series
                            : null,
                    };
                })
                .filter((d) =>
                    isDataSupported(d, country, DATA_SOURCES.TIKTOK),
                );
        }, [metricsData, parseAllTikTokData, tikTokDataMap]);
        const {
            filters,
            filtersKey,
            sortOptionKey,
            maxSortMetric,
            onSearchInputChange,
            setFilter,
            setFilters,
            resetFilters,
            initialSortOption,
            sortOption,
            setSortOption: baseSetSortOption,
            sortedData,
        } = useCardsFilterSort({
            rootCategoryId: watchlistId,
            data: unifiedTimeSeriesAndMetricsData,
            sortOptions: tiktokSortOptions,
            initialSortOption: defaultInitialSortOption,
            filterMissingData: false,
        });
        const TABLE_NAME = 'watchlist-tiktok';
        const { visibleOrderedMetrics, columnsMenu, setSortOption } =
            useTableColumns({
                tableName: TABLE_NAME,
                metrics: tiktokMetrics,
                setSortOption: baseSetSortOption,
            });
        const setWatchlistSortOption = useCallback(
            (sortOption) => {
                setSortOption(
                    tiktokSortOptions.find(
                        ({ sortBy, ascending }) =>
                            sortBy === sortOption.sortBy &&
                            ascending === sortOption.ascending,
                    ),
                );
            },
            [setSortOption],
        );

        const visibleColumnsConfig = createDefaultColumns(
            ['name', ...visibleOrderedMetrics],
            { showHealthStatLabels: true },
        );

        visibleColumnsConfig[0].formatCell = ({ row, value }) => (
            <NameCellWithTooltip row={row} value={value} />
        );

        const [visibleIds, setVisibleIds] = useState(null);
        const [colorsById, setColorsById] = useState(null);
        const [removedColor, setRemovedColor] = useState(null);
        const [viewSumTimeSeries, setViewSumTimeSeries] = useState(false);

        useEffect(() => {
            setVisibleIds(null);
            setColorsById(null);
            setRemovedColor(null);
        }, [filters]);

        const data = [];
        const visibleTimeSeriesData = [];
        const timeSeriesDataIds = [];
        if (sortedData) {
            for (let i = 0; i < sortedData.length; i++) {
                const row = sortedData[i];
                const hasTimeSeriesData =
                    row.timeSeries && row.timeSeries.length;
                const isInitialId =
                    !visibleIds &&
                    visibleTimeSeriesData.length < INITIAL_NUM_LINES &&
                    hasTimeSeriesData;
                const isVisibleId = visibleIds && visibleIds.includes(row.id);
                const checked = !!(isInitialId || isVisibleId);
                const color =
                    hasTimeSeriesData && checked
                        ? (colorsById && colorsById[row.id]) ||
                          getSeriesColors(visibleTimeSeriesData.length)
                        : undefined;
                const formattedRow = { ...row, checked, color };
                data.push(formattedRow);
                if (checked && hasTimeSeriesData)
                    visibleTimeSeriesData.push(formattedRow);
                if (hasTimeSeriesData) timeSeriesDataIds.push(row.id);
            }
        }

        const onToggleCheck = ({ node, checked }) => {
            const nextColorsById = colorsById
                ? { ...colorsById }
                : data.reduce((acc, { id, color }, idx) => {
                      if (color) acc[id] = color;
                      return acc;
                  }, {});

            if (!node) {
                const currentVisible = (
                    !visibleIds
                        ? data.filter(({ checked }) => checked)
                        : visibleIds
                ).length;
                setVisibleIds(
                    !checked && currentVisible < timeSeriesDataIds.length
                        ? timeSeriesDataIds
                        : !checked
                          ? []
                          : null,
                );
                setColorsById(null);
            } else if (checked && visibleIds && !visibleIds.includes(node.id)) {
                setVisibleIds(visibleIds.concat([node.id]));
                nextColorsById[node.id] =
                    removedColor || getSeriesColors(visibleIds.length);
                setColorsById(nextColorsById);
                if (removedColor) setRemovedColor(null);
            } else if (!checked && visibleIds && visibleIds.includes(node.id)) {
                setVisibleIds(visibleIds.filter((id) => id !== node.id));
                setRemovedColor(nextColorsById[node.id]);
                delete nextColorsById[node.id];
                setColorsById(nextColorsById);
            } else if (checked) {
                setVisibleIds(
                    visibleTimeSeriesData.map(({ id }) => id).concat([node.id]),
                );
                nextColorsById[node.id] =
                    removedColor ||
                    getSeriesColors(visibleTimeSeriesData.length);
                setColorsById(nextColorsById);
                if (removedColor) setRemovedColor(null);
            } else {
                setVisibleIds(
                    visibleTimeSeriesData
                        .filter(({ id }) => id !== node.id)
                        .map(({ id }) => id),
                );
                setRemovedColor(nextColorsById[node.id]);
                delete nextColorsById[node.id];
                setColorsById(nextColorsById);
            }
        };

        const unfilteredCount = unifiedTimeSeriesAndMetricsData?.filter(
            ({ time_series }) => time_series && time_series.length,
        ).length;
        const visibleCount = visibleTimeSeriesData
            ? visibleTimeSeriesData.length
            : 0;
        const noDataMessage = !data
            ? ''
            : sortedData.length && !unifiedTimeSeriesAndMetricsData.length
              ? `Not enough data to render time series chart.`
              : `No trends available.${Object.keys(filters).length ? ' Try adjusting the filters.' : ''}`;
        const mixpanelMeta = {
            contentKey: 'TiktokWatchlist',
            country: country ? country.name : null,
        };
        const renderDrawerContent = (selectedId) => {
            const dataPoint =
                selectedId &&
                sortedData &&
                sortedData.find(({ id }) => id === selectedId);
            if (!dataPoint) return <Message>Item not found.</Message>;
            if (dataPoint.type === 'hashtag') {
                return (
                    <ErrorBoundary>
                        <TikTokDrawerContentIndividualHashtag
                            hashtag={dataPoint.name}
                            initialData={dataPoint}
                            category={dataPoint.category}
                            brand={dataPoint.brand}
                            showAddToWatchlist={false}
                        />
                    </ErrorBoundary>
                );
            } else if (dataPoint.type === 'related-category') {
                const isRelatedBrand = dataPoint.relatedType === 'brand';
                return (
                    <ErrorBoundary>
                        <RelatedAggregateDrawerContent
                            entity={isRelatedBrand ? 'brand' : 'category'}
                            selectedRow={dataPoint}
                            week={week}
                            year={year}
                            countries={country?.id ? [country.id] : null}
                            categories={
                                dataPoint.categoryId
                                    ? [dataPoint.categoryId]
                                    : null
                            }
                            brands={
                                dataPoint.brandId ? [dataPoint.brandId] : null
                            }
                            relatedCategories={
                                !isRelatedBrand && dataPoint.relatedCategoryId
                                    ? [dataPoint.relatedCategoryId]
                                    : null
                            }
                            relatedBrands={
                                isRelatedBrand && dataPoint.relatedCategoryId
                                    ? [dataPoint.relatedCategoryId]
                                    : null
                            }
                            name={dataPoint.name}
                            parentName={dataPoint.relatedName}
                            showAddToWatchlist={false}
                        />
                    </ErrorBoundary>
                );
            }
            return (
                <ErrorBoundary>
                    <TikTokDrawerContent
                        paginateBy={
                            dataPoint.category ? 'categories' : 'brands'
                        }
                        dataPoint={dataPoint}
                        parentHierarchies={dataPoint?.hierarchies}
                        showAddToWatchlist={false}
                    />
                </ErrorBoundary>
            );
        };
        const rowHeight = sortedData?.find(
            (s) =>
                (s.type === 'hashtag' && s.brand) ||
                s.type === 'related-category',
        )
            ? 75
            : 50;

        return (
            <Stack $space="1.5rem">
                <div
                    css={`
                        padding: 1.25rem;
                        padding-top: 0;
                        margin: 0 -1.25rem 1.25rem;
                        border-bottom: 1px solid ${({ theme }) => theme.gray_5};
                        display: flex;
                        justify-content: space-between;
                    `}
                >
                    <IconButton
                        size="sm"
                        IconLeft={
                            viewSumTimeSeries
                                ? MdOutlineStackedLineChart
                                : MdOutlineShowChart
                        }
                        onClick={(e) =>
                            setViewSumTimeSeries(!viewSumTimeSeries)
                        }
                        mixpanelContext={
                            viewSumTimeSeries
                                ? 'View Separated Time Series'
                                : 'View Combined Time Series'
                        }
                        isDisabled={!visibleCount}
                    >
                        {viewSumTimeSeries ? 'View Separated' : 'View Combined'}
                    </IconButton>
                    <DownloadCSVMenuButton
                        isDisabled={!isDataLoaded || !date}
                        isLoading={!isDataLoaded || !date}
                        options={[
                            {
                                mixpanelMeta: { label: 'card data csv' },
                                children: 'Table Data CSV',
                                onClick: () =>
                                    downloadItemsAsCSV(
                                        buildExportFilename({
                                            entity: watchlistName,
                                            country,
                                            source: 'tiktok',
                                            type: 'trend',
                                            kind: 'data',
                                            date,
                                        }),
                                        sortedData,
                                        [
                                            {
                                                key: 'name',
                                                accessor: ({
                                                    exportName,
                                                    name,
                                                }) => exportName || name || '',
                                            },
                                            ...visibleOrderedMetrics.map(
                                                ({
                                                    key,
                                                    tableHeadColumnLabel: label,
                                                }) => ({ key, label }),
                                            ),
                                            {
                                                key: 'cluster',
                                                label: 'Cluster',
                                                accessor: ({ cluster }) =>
                                                    cluster
                                                        ? cluster.replace(
                                                              'tiktok ',
                                                              '',
                                                          )
                                                        : cluster,
                                            },
                                            { key: 'healthStats' },
                                        ],
                                    ),
                                downloadType: downloadTypes.CSV,
                                key: 'card data csv',
                            },
                            {
                                mixpanelMeta: { label: 'time series csv' },
                                children: 'Time Series CSV',
                                onClick: () =>
                                    downloadTimeSeriesAsCSV(
                                        buildExportFilename({
                                            entity: watchlistName,
                                            country,
                                            source: 'tiktok',
                                            type: 'trend',
                                            kind: 'time-series',
                                            date,
                                        }),
                                        sortedData,
                                        {
                                            tsColumns: [
                                                { key: 'date' },
                                                {
                                                    key: 'real_volume',
                                                    label: 'volume',
                                                },
                                                {
                                                    key: 'backfill',
                                                    label: 'estimated',
                                                },
                                            ],
                                        },
                                    ),
                                downloadType: downloadTypes.CSV,
                                key: 'time series csv',
                            },
                            {
                                mixpanelMeta: {
                                    label: 'time series image',
                                },
                                children: 'Time Series Image',
                                onClick: () =>
                                    downloadImage(timeSeriesRef.current, {
                                        title: `${watchlistName} (${country ? country.code || country.name : null})`,
                                        filename: buildExportFilename({
                                            date,
                                            country,
                                            source: 'tiktok',
                                            entity: watchlistName,
                                            type: 'trend',
                                            kind: 'time-series',
                                        }),
                                    }),
                                downloadType: downloadTypes.IMAGE,
                                key: 'time series image',
                            },
                        ]}
                        mixpanelMeta={mixpanelMeta}
                    />
                </div>
                <WatchlistTikTokTimeSeries
                    data={visibleTimeSeriesData}
                    metrics={
                        visibleTimeSeriesData.length
                            ? calculateTikTokData(visibleTimeSeriesData)
                            : {}
                    }
                    isLoading={!isDataLoaded}
                    ref={timeSeriesRef}
                    viewSumTimeSeries={viewSumTimeSeries}
                    chartHeight={350}
                    week={week}
                    year={year}
                    showStatDisplay
                    showDataSourceBar={false}
                    customCss="display: block;"
                />
                <div
                    css={`
                        padding: ${({ theme }) => theme.space_8}px 0;
                        font-weight: bold;
                        font-size: 0.75em;
                        margin-bottom: 12px;
                    `}
                >
                    {`Showing ${!isDataLoaded ? '... of ...' : `${visibleCount} of ${unfilteredCount}`} items. Use the list to add and remove items.`}
                </div>
                <Stack $space={(theme) => `${theme.space_10}px;`}>
                    <TableTools
                        css={`
                            display: flex;
                            justify-content: space-between;
                        `}
                    >
                        <FilterSelect
                            filters={filters}
                            setFilter={setFilter}
                            setFilters={setFilters}
                            resetFilters={resetFilters}
                            sortOption={sortOption}
                            setSortOption={setWatchlistSortOption}
                            initialSortOption={initialSortOption}
                            mixpanelMeta={mixpanelMeta}
                            menus={['tikTokCluster', 'healthStats']}
                            healthStatFilterOptions={
                                tikTokHealthStatFilterOptions
                            }
                            getOnChange={getOnChange}
                            showPresetsMenu={false}
                            showAllHealthStats={true}
                            menuAlign="left"
                        />
                        <div
                            css={`
                                display: flex;
                                gap: 12px;
                            `}
                        >
                            {columnsMenu}
                            {renderHits ? (
                                <AddBySearch
                                    countryTextId={countryTextId}
                                    renderHits={renderHits}
                                    panelAlign="right"
                                    dataSource="tiktok"
                                />
                            ) : null}
                        </div>
                    </TableTools>
                    <DataTable
                        dataKey={`d-*-s-${sortOptionKey}-f-${filtersKey}`}
                        week={week}
                        year={year}
                        maxSortMetric={maxSortMetric}
                        onSearchInputChange={onSearchInputChange}
                        sortOption={sortOption}
                        setSortOption={setWatchlistSortOption}
                        initialSortOption={initialSortOption}
                        mixpanelMeta={mixpanelMeta}
                        noDataMessage={noDataMessage}
                        rowHeight={rowHeight}
                        data={isDataLoaded || data?.length ? data : undefined}
                        name={watchlistName || TABLE_NAME}
                        onRemoveItem={handleQueryCacheOnRemoveItem}
                        onToggleCheck={onToggleCheck}
                        columns={visibleColumnsConfig}
                        renderDrawerContent={renderDrawerContent}
                        DrawerComponent={Drawer}
                        BannerRowContent={
                            <BannerRowContent
                                watchlistId={watchlistId}
                                mixpanelMeta={mixpanelMeta}
                            />
                        }
                    />
                </Stack>
            </Stack>
        );
    },
    isEqual,
);

export default TiktokWatchlist;
